import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAgentModal } from '../../context/AgentModalContext';
import { getAgent } from '../../config/agentConfig';
import { useOrchestratorAgent } from '../../context/OrchestratorAgentContext';
import {
  conversationSummaryService,
  sessionMetadataService,
  userMemoryService,
  contextCompiler,
} from '../../services/memory';
import StreamingMarkdown from '../Tasks/StreamingMarkdown';
import { ProfessorService } from '../../services/llmClient/ProfessorService';
import { TasksAgentService } from '../../services/llmClient/TasksAgentService';
import eventStreamService from '../../services/supabaseClient/eventStreamService';
import { generateCourseFromPromptThunk } from '../../redux/courseDetailsSlice';
import { selectProfile } from '../../redux/profileSlice';
import { selectActiveCourse, selectActiveLesson } from '../../redux/classroomSlice';
import { selectContentByActivityId } from '../../redux/contentSlice';
import { selectAllActivities } from '../../redux/activitiesSlice';
import './AgentModal.css';

const toScopeFragment = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const createThreadId = () => `thread_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getThreadListStorageKey = (componentType) => `agent_modal_threads_${componentType}`;

const getActiveThreadStorageKey = (componentType) => `agent_modal_active_thread_${componentType}`;

const buildThreadComponentType = (componentType, threadId) =>
  threadId ? `${componentType}__${threadId}` : componentType;

const truncateThreadText = (value, maxLength = 64) => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
};

const buildThreadTitle = (messages, fallbackTimestamp = new Date().toISOString()) => {
  const firstUserMessage = messages.find(
    (message) => message.sender === 'user' && !message.isSummary
  );
  if (firstUserMessage?.text) {
    return truncateThreadText(firstUserMessage.text, 40);
  }

  return `Chat ${new Date(fallbackTimestamp).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

const AgentModal = () => {
  const dispatch = useDispatch();
  const { state, closeAgentModal, switchAgentModal } = useAgentModal();
  const { sendToAgent } = useOrchestratorAgent();
  const switchableAgents = [
    'orchestrator',
    'professor',
    'tasks',
    'projectAssistant',
    'courseCreator',
  ];

  // Redux state for context
  const studentProfile = useSelector(selectProfile);
  const activeCourse = useSelector(selectActiveCourse);
  const activeLesson = useSelector(selectActiveLesson);
  const activityContent = useSelector((reduxState) =>
    selectContentByActivityId(reduxState, state.contextPayload?.activityId)
  );
  const allActivities = useSelector(selectAllActivities);

  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [streamingMessageIndex, setStreamingMessageIndex] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const [autoSpeakEnabled, setAutoSpeakEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('agentAutoSpeakEnabled');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  const [isMinimized, setIsMinimized] = useState(false);
  const [threadList, setThreadList] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const sendMessageRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const pendingSpeechRef = useRef('');

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && state.isOpen) {
        closeAgentModal();
      }
    };

    if (state.isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [state.isOpen, closeAgentModal]);

  useEffect(() => {
    if (!state.isOpen) return;
    setIsMinimized(false);
  }, [state.isOpen, state.agentId]);

  useEffect(() => {
    try {
      localStorage.setItem('agentAutoSpeakEnabled', String(autoSpeakEnabled));
    } catch {
      // noop
    }
  }, [autoSpeakEnabled]);

  const resolveComponentType = useCallback(() => {
    const baseAgentId = toScopeFragment(state.agentId) || 'unknown_agent';
    const originKey = toScopeFragment(state.origin) || 'global';
    const context = state.contextPayload || {};
    const contextCandidates = [
      context.activityId,
      context.lessonId,
      context.lessonTitle,
      context.projectId,
      context.projectTitle,
      context.courseId,
      context.courseTitle,
      context.taskId,
      context.taskTitle,
      context.currentPath,
    ]
      .map(toScopeFragment)
      .filter(Boolean);

    const scopeKey = contextCandidates[0] || 'default';
    return `agent_${baseAgentId}_${originKey}_${scopeKey}`;
  }, [state.agentId, state.contextPayload, state.origin]);

  const componentType = buildThreadComponentType(resolveComponentType(), activeThreadId);

  const persistThreadState = useCallback(
    (nextThreads, nextActiveThreadId) => {
      const baseComponentType = resolveComponentType();
      setThreadList(nextThreads);
      setActiveThreadId(nextActiveThreadId);

      try {
        localStorage.setItem(
          getThreadListStorageKey(baseComponentType),
          JSON.stringify(nextThreads)
        );
        localStorage.setItem(getActiveThreadStorageKey(baseComponentType), nextActiveThreadId);
      } catch {
        // noop
      }
    },
    [resolveComponentType]
  );

  const handleSendMessageFromText = useCallback(
    async (textToSend) => {
      const outgoingText = String(textToSend || '').trim();
      if (!outgoingText || isLoadingResponse || !state.agentId || !activeThreadId) return;

      const agent = getAgent(state.agentId);
      const userMessage = {
        sender: 'user',
        text: outgoingText,
        timestamp: new Date().toLocaleTimeString(),
      };
      const conversationKey = componentType;
      const nextChatMessages = [...chatMessages, userMessage];
      const normalizedChatHistory = nextChatMessages
        .filter((msg) => !msg.isSummary)
        .map((msg) => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text,
        }));

      setChatMessages((prev) => [...prev, userMessage]);
      setChatInput('');
      setIsLoadingResponse(true);
      conversationSummaryService.addMessage(componentType, 'user', userMessage.text);
      void eventStreamService.emitEvent({
        userId: studentProfile?.id,
        sessionId: conversationKey,
        conversationKey,
        streamKey: conversationKey,
        eventFamily: 'chat',
        eventType: 'chat_message_sent',
        eventName: `${state.agentId}_message_sent`,
        origin: state.origin || 'agent_modal',
        actorType: 'user',
        actorId: studentProfile?.id || null,
        entityType: 'agent',
        entityId: state.agentId,
        courseId: activeCourse?.id || null,
        lessonId: activeLesson?.id || null,
        activityId: state.contextPayload?.activityId || null,
        messageRole: 'user',
        payload: {
          textLength: outgoingText.length,
          lessonTitle: state.contextPayload?.lessonTitle || activeLesson?.title || null,
          courseTitle: state.contextPayload?.courseTitle || activeCourse?.title || null,
        },
      });

      try {
        let reply;

        if (state.agentId === 'professor') {
          const lessonTitle =
            state.contextPayload?.lessonTitle || activeLesson?.title || 'Current Lesson';
          const activityId = state.contextPayload?.activityId || 'modal-session';
          const leader = state.contextPayload?.activityLeader || 'Professor';
          const notes = activityContent?.notes || '';

          const courseDetails = {
            title: state.contextPayload?.courseTitle || activeCourse?.title,
            description: activeCourse?.description,
            difficulty_level: activeCourse?.difficultyLevel,
          };

          const professorReply = await ProfessorService.processStudentMessage(
            activityId,
            lessonTitle,
            leader,
            outgoingText,
            studentProfile,
            courseDetails,
            notes,
            normalizedChatHistory
          );

          reply = professorReply.text;
        } else if (state.agentId === 'tasks') {
          const today = new Date().toDateString();
          const currentTasks = allActivities
            .filter((task) => !task.completed)
            .map((task) => ({
              id: task.id,
              title: task.what || task.activity_what,
              description: task.how || task.activity_how,
              dueDate: task.due_date,
              urgency: task.urgency_level,
              estimatedMinutes: task.estimated_minutes,
              projectId: task.projectId,
              courseId: task.courseId,
            }));

          const completedToday = allActivities.filter(
            (task) => task.completed && new Date(task.updated_at).toDateString() === today
          ).length;

          const pendingUrgent = allActivities.filter(
            (task) => !task.completed && task.urgency_level === 'high'
          ).length;

          const upcomingDeadlines = allActivities
            .filter((task) => !task.completed && task.due_date)
            .map((task) => ({
              taskId: task.id,
              taskTitle: task.what || task.activity_what,
              dueDate: task.due_date,
            }))
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
            .slice(0, 5);

          const sessionMetadata = sessionMetadataService.getMetadata(componentType);
          const userMemory = userMemoryService.formatForContext();
          const conversationSummaries = conversationSummaryService.formatForContext(componentType);

          const focusedTask = state.contextPayload?.focusMode
            ? {
                id: state.contextPayload.taskId,
                title: state.contextPayload.taskTitle,
                description: state.contextPayload.taskDescription,
                dueDate: state.contextPayload.dueDate,
                estimatedMinutes: state.contextPayload.estimatedMinutes,
                isRunning: state.contextPayload.isRunning,
                completed: state.contextPayload.completed || false,
              }
            : null;

          const taskMode = focusedTask?.completed ? 'reflection' : 'active';
          const systemPromptOverride = agent.modes?.[taskMode]?.systemPrompt || null;

          const tasksReply = await TasksAgentService.chatWithTasksAssistant(outgoingText, {
            userId: studentProfile?.id,
            userName: studentProfile?.name,
            userProfile: studentProfile,
            chatHistory: normalizedChatHistory,
            sessionMetadata,
            userMemory,
            conversationSummaries,
            mode: taskMode,
            systemPromptOverride,
            currentTasks,
            completedToday,
            pendingUrgent,
            upcomingDeadlines,
            focusedTask,
          });

          reply = tasksReply.response || tasksReply.text;
        } else if (state.agentId === 'courseCreator') {
          const action = await dispatch(
            generateCourseFromPromptThunk({
              prompt: outgoingText,
              profileData: studentProfile,
            })
          );

          if (generateCourseFromPromptThunk.fulfilled.match(action)) {
            const createdCourse = action.payload;
            reply = `I created "${createdCourse?.title || 'your course'}" from that prompt and saved it. You can find it on your Dashboard now.`;
            setTimeout(() => {
              closeAgentModal();
              const nextCourseId = createdCourse?.id || '';
              const nextUrl = nextCourseId
                ? `/dashboard?openCourseId=${encodeURIComponent(nextCourseId)}`
                : '/dashboard';
              window.location.assign(nextUrl);
            }, 350);
          } else {
            reply =
              action.payload ||
              action.error?.message ||
              'I could not create that course from the prompt.';
          }
        } else {
          const contextMessages = await contextCompiler.buildContextMessages({
            userId: studentProfile?.id,
            componentType,
            latestMessage: outgoingText,
            componentContext: {
              origin: state.origin,
              agentType: state.agentId,
              ...state.contextPayload,
            },
          });

          if (contextMessages.length > 0 && contextMessages[0].role === 'system') {
            contextMessages[0].content = `${agent.systemPrompt}\n\n${contextMessages[0].content}`;
          } else {
            contextMessages.unshift({
              role: 'system',
              content: agent.systemPrompt,
            });
          }

          const fullMessage = contextMessages
            .map((msg) => `${msg.role}: ${msg.content}`)
            .join('\n\n');
          const response = await sendToAgent(fullMessage);
          reply = response.reply;
        }

        if (reply) {
          const aiMessage = {
            sender: 'assistant',
            text: reply,
            timestamp: new Date().toLocaleTimeString(),
            isStreaming: true,
          };
          setChatMessages((prev) => [...prev, aiMessage]);
          setStreamingMessageIndex(chatMessages.length + 1);
          conversationSummaryService.addMessage(componentType, 'assistant', aiMessage.text);
          void eventStreamService.emitEvent({
            userId: studentProfile?.id,
            sessionId: conversationKey,
            conversationKey,
            streamKey: conversationKey,
            eventFamily: 'chat',
            eventType: 'chat_message_received',
            eventName: `${state.agentId}_message_received`,
            origin: state.origin || 'agent_modal',
            actorType: 'assistant',
            actorId: state.agentId,
            entityType: 'agent',
            entityId: state.agentId,
            courseId: activeCourse?.id || null,
            lessonId: activeLesson?.id || null,
            activityId: state.contextPayload?.activityId || null,
            messageRole: 'assistant',
            success: true,
            payload: {
              textLength: reply.length,
              lessonTitle: state.contextPayload?.lessonTitle || activeLesson?.title || null,
              courseTitle: state.contextPayload?.courseTitle || activeCourse?.title || null,
            },
          });
        }
      } catch (error) {
        const errorMessage = {
          sender: 'assistant',
          text: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date().toLocaleTimeString(),
          isError: true,
        };
        setChatMessages((prev) => [...prev, { ...errorMessage, isStreaming: false }]);
        conversationSummaryService.addMessage(componentType, 'assistant', errorMessage.text, {
          isError: true,
        });
        void eventStreamService.emitEvent({
          userId: studentProfile?.id,
          sessionId: conversationKey,
          conversationKey,
          streamKey: conversationKey,
          eventFamily: 'chat',
          eventType: 'chat_message_failed',
          eventName: `${state.agentId}_message_failed`,
          origin: state.origin || 'agent_modal',
          actorType: 'assistant',
          actorId: state.agentId,
          entityType: 'agent',
          entityId: state.agentId,
          courseId: activeCourse?.id || null,
          lessonId: activeLesson?.id || null,
          activityId: state.contextPayload?.activityId || null,
          messageRole: 'assistant',
          success: false,
          errorCode: error?.message || 'chat_failed',
          payload: {
            textLength: outgoingText.length,
          },
        });
      } finally {
        setIsLoadingResponse(false);
      }
    },
    [
      activeThreadId,
      componentType,
      isLoadingResponse,
      state.agentId,
      state.contextPayload,
      state.origin,
      activeLesson?.id,
      activeLesson?.title,
      activityContent?.notes,
      chatMessages,
      closeAgentModal,
      dispatch,
      sendToAgent,
      studentProfile,
      activeCourse,
      allActivities,
    ]
  );

  useEffect(() => {
    sendMessageRef.current = handleSendMessageFromText;
  }, [handleSendMessageFromText]);

  // Initialize Speech Recognition
  useEffect(() => {
    const synth = synthRef.current;
    const clearSilenceTimer = () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };

    const resetSilenceTimer = () => {
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        const text = String(pendingSpeechRef.current || '').trim();
        if (!text || typeof sendMessageRef.current !== 'function') return;

        try {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        } catch {
          // noop
        }
        setIsRecording(false);
        sendMessageRef.current(text);
      }, 4000);
    };

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const speechResult = Array.from(event.results)
          .map((result) => result?.[0]?.transcript || '')
          .join(' ')
          .trim();
        if (!speechResult) return;
        pendingSpeechRef.current = speechResult;
        setChatInput(speechResult);
        resetSilenceTimer();
      };

      recognitionRef.current.onerror = (event) => {
        const code = event?.error || 'unknown';
        const labelMap = {
          'not-allowed': 'Microphone permission denied. Allow microphone access and try again.',
          'service-not-allowed': 'Speech service blocked by browser permissions.',
          'no-speech': 'No speech detected. Try speaking clearly and closer to the microphone.',
          'audio-capture': 'No microphone detected. Check your audio input device.',
          network: 'Speech recognition network error. Check your connection and retry.',
          aborted: 'Speech recognition was interrupted.',
        };
        setSpeechError(labelMap[code] || `Speech recognition error: ${code}`);
        setIsRecording(false);
        clearSilenceTimer();
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
        clearSilenceTimer();
      };
    } else {
      setSpeechError(
        'Speech recognition is not supported in this browser. Use Chrome/Edge over HTTPS or localhost.'
      );
    }

    return () => {
      clearSilenceTimer();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synth) {
        synth.cancel();
      }
    };
  }, []);

  // Auto-speak new AI messages
  useEffect(() => {
    if (chatMessages.length === 0) return;

    const lastMessage = chatMessages[chatMessages.length - 1];

    // Only speak assistant messages (not user messages)
    if (autoSpeakEnabled && lastMessage.sender === 'assistant' && !lastMessage.isSummary) {
      speakText(lastMessage.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages.length, autoSpeakEnabled]);

  // Initialize memory services when modal opens
  useEffect(() => {
    if (state.isOpen && state.agentId && studentProfile?.id) {
      const initializeMemory = async () => {
        // Initialize session metadata
        await sessionMetadataService.initializeSession(componentType, studentProfile.id);

        // Initialize user memory
        await userMemoryService.initialize(studentProfile.id);

        // Load recent conversation summaries
        await conversationSummaryService.loadRecentSummaries(studentProfile.id, componentType);
      };

      initializeMemory();
    }
  }, [state.isOpen, state.agentId, studentProfile, componentType]);

  useEffect(() => {
    if (!(state.isOpen && state.agentId)) return;

    const baseComponentType = resolveComponentType();
    let persistedThreads = [];

    try {
      persistedThreads =
        JSON.parse(localStorage.getItem(getThreadListStorageKey(baseComponentType))) || [];
    } catch {
      persistedThreads = [];
    }

    if (!Array.isArray(persistedThreads) || persistedThreads.length === 0) {
      const now = new Date().toISOString();
      persistedThreads = [
        {
          id: createThreadId(),
          title: buildThreadTitle([], now),
          preview: 'Fresh conversation',
          createdAt: now,
          updatedAt: now,
          messageCount: 0,
        },
      ];
    }

    const persistedActiveThreadId = localStorage.getItem(
      getActiveThreadStorageKey(baseComponentType)
    );
    const nextActiveThreadId = persistedThreads.some(
      (thread) => thread.id === persistedActiveThreadId
    )
      ? persistedActiveThreadId
      : persistedThreads[0].id;

    persistThreadState(persistedThreads, nextActiveThreadId);
  }, [
    state.isOpen,
    state.agentId,
    state.contextPayload,
    state.origin,
    resolveComponentType,
    persistThreadState,
  ]);

  // Load persisted messages when modal opens or agent changes
  useEffect(() => {
    if (state.isOpen && state.agentId && activeThreadId) {
      // Load persisted messages
      conversationSummaryService.loadSessionFromLocalStorage(componentType);
      const persistedMessages = conversationSummaryService.getCurrentSession(componentType);

      // Check if we need to regenerate welcome message (detect old format)
      let needsNewWelcome = false;
      if (persistedMessages && persistedMessages.length > 0) {
        const firstMessage = persistedMessages[0];
        // Check if first message is old welcome format (contains "How can I help you with this lesson?")
        if (
          firstMessage.metadata?.isSummary &&
          firstMessage.content.includes('How can I help you with this lesson?')
        ) {
          needsNewWelcome = true;
        }
      }

      if (persistedMessages && persistedMessages.length > 0 && !needsNewWelcome) {
        // Convert from service format to component format
        const formattedMessages = persistedMessages.map((msg) => ({
          sender: msg.role === 'user' ? 'user' : 'assistant',
          text: msg.content,
          timestamp: new Date(msg.timestamp).toLocaleTimeString(),
          isSummary: msg.metadata?.isSummary || false,
          isError: msg.metadata?.isError || false,
        }));
        setChatMessages(formattedMessages);
      } else {
        // First time opening - add welcome message
        const agent = getAgent(state.agentId);

        // Create context-aware welcome message
        let contextInfo = '';
        let helpPrompt = '';

        // Agent-specific context and prompts
        switch (state.agentId) {
          case 'professor':
            if (state.contextPayload) {
              const { courseTitle, lessonTitle, projectTitle } = state.contextPayload;
              if (lessonTitle) {
                contextInfo = `\n\n📚 **Current Lesson:** ${lessonTitle}${courseTitle ? ` (${courseTitle})` : ''}`;
                helpPrompt = 'What would you like to learn about this lesson?';
              } else if (projectTitle) {
                contextInfo = `\n\n📚 **Current Project:** ${projectTitle}`;
                helpPrompt = 'How can I help with your project?';
              } else {
                helpPrompt = 'What concepts would you like me to explain?';
              }
            } else {
              helpPrompt = 'What would you like to learn today?';
            }
            break;

          case 'coach':
            helpPrompt = "What challenge are you working on? Let's push your thinking further.";
            break;

          case 'peer':
            helpPrompt = "What ideas are you exploring? Let's think through this together.";
            break;

          case 'mirror':
            helpPrompt = 'How are you feeling about your learning journey?';
            break;

          case 'tasks':
            if (state.contextPayload?.focusMode && state.contextPayload?.taskTitle) {
              // Focused on a specific task
              if (state.contextPayload.completed) {
                // Reflection mode - task is completed
                contextInfo = `\n\n✅ **Completed Task:** ${state.contextPayload.taskTitle}`;
                if (state.contextPayload.estimatedMinutes) {
                  contextInfo += `\n⏱️ **Estimated:** ${state.contextPayload.estimatedMinutes} min`;
                }
                helpPrompt =
                  "Great work completing this task! Let's reflect on how it went. What went well? What challenges did you face?";
              } else {
                // Active mode - task is in progress
                const taskStatus = state.contextPayload.isRunning ? '▶️ Running' : '⏸️ Not Started';
                contextInfo = `\n\n✓ **Focused Task:** ${state.contextPayload.taskTitle}`;
                if (state.contextPayload.dueDate) {
                  contextInfo += `\n📅 **Due:** ${new Date(state.contextPayload.dueDate).toLocaleDateString()}`;
                }
                if (state.contextPayload.estimatedMinutes) {
                  contextInfo += `\n⏱️ **Estimated:** ${state.contextPayload.estimatedMinutes} min`;
                }
                contextInfo += `\n${taskStatus}`;
                helpPrompt =
                  'I&apos;m here to help you work on this task. Tell me what you&apos;re trying to accomplish, and I&apos;ll help break it down into steps.';
              }
            } else if (state.contextPayload?.isRunning && state.contextPayload?.taskTitle) {
              // Running task (from sidebar)
              contextInfo = `\n\n▶️ **Running Task:** ${state.contextPayload.taskTitle}`;
              helpPrompt = 'How can I help you stay on track?';
            } else {
              // General task overview
              helpPrompt =
                'I can help you with task planning, prioritization, due dates, and tracking your accomplishments. What would you like to know?';
            }
            break;

          case 'librarian':
            helpPrompt = 'What resources or references can I help you find?';
            break;

          case 'courseCreator':
            helpPrompt = 'What kind of course would you like to create?';
            break;

          case 'projectAssistant':
            if (state.contextPayload?.projectsCount !== undefined) {
              contextInfo = `\n\n🚀 **Your Projects:** ${state.contextPayload.projectsCount} total, ${state.contextPayload.activeProjects || 0} active`;
              helpPrompt = 'How can I help you manage your projects?';
            } else {
              helpPrompt = 'What project would you like to work on?';
            }
            break;

          case 'orchestrator':
            if (state.contextPayload?.currentPath) {
              contextInfo = `\n\n🧭 **Current Location:** ${state.contextPayload.currentPath}`;
              helpPrompt = 'Where would you like to go, or what would you like to do?';
            } else {
              helpPrompt = 'How can I help you navigate the platform?';
            }
            break;

          default:
            helpPrompt = 'How can I assist you today?';
        }

        const welcomeMessage = {
          sender: 'assistant',
          text: `👋 **Welcome!**\n\nI'm your ${agent.label} agent. ${agent.description}${contextInfo}\n\n${helpPrompt}`,
          timestamp: new Date().toLocaleTimeString(),
          isSummary: true,
          isStreaming: false,
        };

        // If regenerating welcome, clear old session first
        if (needsNewWelcome) {
          conversationSummaryService.clearSession(componentType);
        }

        setChatMessages([welcomeMessage]);

        // Save welcome message
        conversationSummaryService.addMessage(componentType, 'assistant', welcomeMessage.text, {
          isSummary: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.isOpen,
    state.agentId,
    state.contextPayload,
    state.origin,
    componentType,
    activeThreadId,
  ]);

  useEffect(() => {
    if (!activeThreadId) return;

    const preview = truncateThreadText(
      [...chatMessages].reverse().find((message) => !message.isSummary)?.text || '',
      72
    );
    const messageCount = chatMessages.filter((message) => !message.isSummary).length;

    setThreadList((prev) => {
      const nextThreads = prev.map((thread) =>
        thread.id === activeThreadId
          ? {
              ...thread,
              title: buildThreadTitle(chatMessages, thread.createdAt),
              preview: preview || thread.preview || 'Fresh conversation',
              updatedAt: messageCount > 0 ? new Date().toISOString() : thread.updatedAt,
              messageCount,
            }
          : thread
      );

      try {
        localStorage.setItem(
          getThreadListStorageKey(resolveComponentType()),
          JSON.stringify(nextThreads)
        );
      } catch {
        // noop
      }

      return nextThreads;
    });
  }, [activeThreadId, chatMessages, resolveComponentType]);

  const handleSendMessage = async () => {
    await handleSendMessageFromText(chatInput);
  };

  const startRecording = () => {
    if (!recognitionRef.current) {
      setSpeechError(
        'Speech recognition is unavailable in this browser context. Ensure HTTPS/localhost and microphone permissions.'
      );
      return;
    }

    setSpeechError('');
    setIsRecording(true);
    setChatInput('');
    pendingSpeechRef.current = '';

    try {
      recognitionRef.current.start();
    } catch (err) {
      setSpeechError(err?.message || 'Failed to start speech recognition.');
      setIsRecording(false);
    }
  };

  const stopRecording = useCallback(() => {
    pendingSpeechRef.current = '';
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
    }
  }, [isRecording]);

  const speakText = (text) => {
    if (!synthRef.current) {
      return;
    }

    // Cancel any ongoing speech
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => {
      setIsSpeaking(true);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
    };

    synthRef.current.speak(utterance);
  };

  const stopSpeaking = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const toggleAutoSpeak = () => {
    setAutoSpeakEnabled((prev) => {
      const next = !prev;
      if (!next && isSpeaking) {
        stopSpeaking();
      }
      return next;
    });
  };

  const startNewThread = useCallback(() => {
    const now = new Date().toISOString();
    const nextThread = {
      id: createThreadId(),
      title: buildThreadTitle([], now),
      preview: 'Fresh conversation',
      createdAt: now,
      updatedAt: now,
      messageCount: 0,
    };

    persistThreadState([nextThread, ...threadList], nextThread.id);
    setChatMessages([]);
    setChatInput('');
    setStreamingMessageIndex(null);
    setIsLoadingResponse(false);
    stopSpeaking();
    stopRecording();
  }, [persistThreadState, stopRecording, stopSpeaking, threadList]);

  const handleClose = async () => {
    // Stop any ongoing speech
    stopSpeaking();
    stopRecording();

    // Save conversation summary before closing (new memory layer)
    if (studentProfile?.id && state.agentId) {
      try {
        await conversationSummaryService.generateAndSaveSummary(
          studentProfile.id,
          componentType,
          state.contextPayload?.activityId || state.contextPayload?.taskId || null,
          activeThreadId || `session-${Date.now()}`
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save conversation summary:', error);
      }
    }

    // Don't clear messages - they're persisted in localStorage
    closeAgentModal();
  };

  if (!state.isOpen || !state.agentId) return null;

  const agent = getAgent(state.agentId);

  return (
    <div className="agent-modal-backdrop" role="presentation">
      <div
        className={`agent-modal-book ${isMinimized ? 'minimized' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-modal-title"
      >
        {/* Header */}
        <header className="agent-modal-header">
          <div className="agent-chip" data-accent={agent.accentToken}>
            <span className="agent-icon">{agent.icon}</span>
            <span className="agent-label" id="agent-modal-title">
              {agent.label}
            </span>
          </div>
          {!isMinimized && (
            <select
              className="agent-switcher"
              value={state.agentId}
              onChange={(e) => switchAgentModal(e.target.value)}
              aria-label="Switch agent"
              title="Switch agent"
            >
              {switchableAgents.map((agentId) => {
                const optionAgent = getAgent(agentId);
                return (
                  <option key={agentId} value={agentId}>
                    {optionAgent.label}
                  </option>
                );
              })}
            </select>
          )}
          {state.origin && <span className="agent-origin">from {state.origin}</span>}
          <button
            onClick={() => setIsMinimized((prev) => !prev)}
            className="agent-modal-minimize"
            aria-label={isMinimized ? 'Expand panel' : 'Minimize panel'}
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            {isMinimized ? '◻' : '—'}
          </button>
          <button onClick={handleClose} className="agent-modal-close" aria-label="Close">
            ×
          </button>
        </header>

        {/* Main chat area */}
        {!isMinimized && (
          <main className="agent-modal-page">
            <div className="agent-thread-controls">
              <label className="agent-thread-picker">
                <span className="agent-thread-label">Chats</span>
                <select
                  value={activeThreadId || ''}
                  onChange={(e) => {
                    setActiveThreadId(e.target.value);
                    try {
                      localStorage.setItem(
                        getActiveThreadStorageKey(resolveComponentType()),
                        e.target.value
                      );
                    } catch {
                      // noop
                    }
                  }}
                  className="agent-thread-select"
                  aria-label="Select saved chat"
                  disabled={isLoadingResponse}
                >
                  {threadList.map((thread) => (
                    <option key={thread.id} value={thread.id}>
                      {thread.title}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="agent-thread-new"
                onClick={startNewThread}
                disabled={isLoadingResponse}
              >
                New chat
              </button>
            </div>

            {activeThreadId && (
              <div className="agent-thread-meta">
                <span>
                  {threadList.find((thread) => thread.id === activeThreadId)?.preview ||
                    'Fresh conversation'}
                </span>
              </div>
            )}

            {chatMessages.map((message, index) => (
              <div
                key={index}
                className={`agent-message ${message.sender === 'user' ? 'agent-message--user' : 'agent-message--assistant'} ${message.isError ? 'agent-message--error' : ''}`}
              >
                <div className="agent-message-avatar">
                  {message.sender === 'user' ? '👤' : agent.icon}
                </div>
                <div className="agent-message-content">
                  {message.sender === 'assistant' && index === streamingMessageIndex ? (
                    <StreamingMarkdown
                      content={message.text}
                      speed={15}
                      onComplete={() => {
                        setChatMessages((prev) =>
                          prev.map((msg, i) => (i === index ? { ...msg, isStreaming: false } : msg))
                        );
                        setStreamingMessageIndex(null);
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                    />
                  ) : (
                    <div className="agent-message-text">
                      {message.sender === 'user' ? (
                        message.text
                      ) : (
                        <StreamingMarkdown content={message.text} speed={0} />
                      )}
                    </div>
                  )}
                  <span className="agent-message-time">{message.timestamp}</span>
                </div>
              </div>
            ))}

            {isLoadingResponse && (
              <div className="agent-message agent-message--assistant agent-message--loading">
                <div className="agent-message-avatar">{agent.icon}</div>
                <div className="agent-message-content">
                  <div className="agent-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </main>
        )}

        {/* Footer composer */}
        <footer className={`agent-modal-composer ${isMinimized ? 'voice-only' : ''}`}>
          {speechError ? (
            <div className="agent-speech-error" role="alert">
              {speechError}
            </div>
          ) : null}
          {/* Voice input button */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`agent-voice-btn ${isRecording ? 'recording' : ''}`}
            aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
            title={isRecording ? 'Stop recording' : 'Voice input'}
          >
            <i
              className={`fa-solid ${isRecording ? 'fa-stop' : 'fa-microphone'}`}
              aria-hidden="true"
            />
          </button>

          {!isMinimized && (
            <button
              onClick={toggleAutoSpeak}
              className={`agent-voice-btn ${autoSpeakEnabled ? 'auto-enabled' : ''}`}
              aria-label={autoSpeakEnabled ? 'Disable auto voice' : 'Enable auto voice'}
              title={autoSpeakEnabled ? 'Auto voice: on' : 'Auto voice: off'}
            >
              <i className={`fa-solid ${autoSpeakEnabled ? 'fa-toggle-on' : 'fa-toggle-off'}`} />
            </button>
          )}

          {!isMinimized && (
            <textarea
              ref={inputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={`Message ${agent.label}... (Shift+Enter for new line)`}
              disabled={isLoadingResponse}
              className="agent-input"
              rows={1}
              style={{
                resize: 'none',
                minHeight: '44px',
                maxHeight: '200px',
                overflowY: chatInput.split('\n').length > 3 ? 'auto' : 'hidden',
              }}
            />
          )}

          {/* Voice output button */}
          <button
            onClick={isSpeaking ? stopSpeaking : () => {}}
            className={`agent-voice-btn ${isSpeaking ? 'speaking' : ''}`}
            aria-label={isSpeaking ? 'Stop speaking' : 'Text-to-speech'}
            title={isSpeaking ? 'Stop speaking' : 'Text-to-speech'}
            disabled={!isSpeaking}
          >
            <i
              className={`fa-solid ${isSpeaking ? 'fa-volume-xmark' : 'fa-volume-high'}`}
              aria-hidden="true"
            />
          </button>

          {!isMinimized && (
            <button
              onClick={handleSendMessage}
              disabled={!chatInput.trim() || isLoadingResponse}
              className="agent-send-btn"
              aria-label="Send message"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default AgentModal;
